<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import intlTelInput from "intl-tel-input";
import enTranslations from "intl-tel-input/i18n/en";

const props = defineProps<{
  modelValue: string;
  name: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: string): void;
}>();

const { t } = useI18n();

// Country data interface
interface Country {
  code: string;
  name: string;
  callingCode: string;
  flag: string;
}

// Convert ISO2 code to flag emoji (e.g., "NO" -> "🇳🇴")
const iso2ToFlag = (iso2: string): string => {
  const codePoints = iso2
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Priority country codes (shown at the top)
const priorityCodes = ["no", "se", "dk", "fi", "gb", "us", "de", "nl", "pl"];

// Get country data from intl-tel-input
const rawCountryData = intlTelInput.getCountryData();

// Transform intl-tel-input data to our format with translations
const transformedCountries: Country[] = rawCountryData.map((c) => ({
  code: c.iso2.toUpperCase(),
  name: enTranslations[c.iso2] || c.name || c.iso2.toUpperCase(),
  callingCode: `+${c.dialCode}`,
  flag: iso2ToFlag(c.iso2),
}));

// Split into priority and other countries
const priorityCountries = priorityCodes
  .map((code) =>
    transformedCountries.find((c) => c.code.toLowerCase() === code)
  )
  .filter((c): c is Country => c !== undefined);

// Sort countries: priority countries first (in order), then alphabetically by name
const allCountries = [...transformedCountries].sort((a, b) => {
  const aPriority = priorityCodes.indexOf(a.code.toLowerCase());
  const bPriority = priorityCodes.indexOf(b.code.toLowerCase());

  // Both are priority countries - sort by priority order
  if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
  // Only a is priority - it comes first
  if (aPriority !== -1) return -1;
  // Only b is priority - it comes first
  if (bPriority !== -1) return 1;
  // Neither is priority - sort alphabetically
  return a.name.localeCompare(b.name);
});

// State
const isDropdownOpen = ref(false);
const searchQuery = ref("");
const nationalNumber = ref("");
const selectedCountry = ref<Country>(priorityCountries[0]); // Default to Norway
const dropdownRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);
const countryListRef = ref<HTMLElement | null>(null);
const highlightedIndex = ref(-1);
let focusTimeoutId: ReturnType<typeof setTimeout> | null = null;

// Generate unique IDs for ARIA
const dropdownId = `phone-dropdown-${Math.random().toString(36).substr(2, 9)}`;
const listboxId = `phone-listbox-${Math.random().toString(36).substr(2, 9)}`;

// Computed
const filteredCountries = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return allCountries;

  return allCountries.filter(
    (country) =>
      country.name.toLowerCase().includes(query) ||
      country.code.toLowerCase().includes(query) ||
      country.callingCode.includes(query)
  );
});

const fullPhoneNumber = computed(() => {
  const number = nationalNumber.value.replace(/^0+/, "").replace(/\D/g, "");
  return number ? `${selectedCountry.value.callingCode}${number}` : "";
});

// Methods
const detectCountry = () => {
  // Try to detect from browser language
  const nav = navigator as Navigator & { userLanguage?: string };
  const lang = nav.language || nav.userLanguage || "";
  const parts = lang.split("-");

  if (parts.length >= 2) {
    const countryCode = parts[1].toUpperCase();
    const country = allCountries.find((c) => c.code === countryCode);
    if (country) return country;
  }

  // Language code to country mapping
  const langToCountry: Record<string, string> = {
    nb: "NO",
    nn: "NO",
    no: "NO",
    sv: "SE",
    da: "DK",
    fi: "FI",
    de: "DE",
    nl: "NL",
    pl: "PL",
    uk: "UA",
    en: "GB",
    es: "ES",
    fr: "FR",
    it: "IT",
    pt: "PT",
  };

  const langCode = parts[0]?.toLowerCase();
  const countryCode = langToCountry[langCode];
  if (countryCode) {
    const country = allCountries.find((c) => c.code === countryCode);
    if (country) return country;
  }

  // Default to Norway
  return priorityCountries[0];
};

const parseExistingNumber = (phoneNumber: string) => {
  if (!phoneNumber) return;

  // Try to match a calling code
  for (const country of allCountries) {
    if (phoneNumber.startsWith(country.callingCode)) {
      selectedCountry.value = country;
      nationalNumber.value = phoneNumber.slice(country.callingCode.length);
      return;
    }
  }

  // If it starts with +, try to extract the country code
  if (phoneNumber.startsWith("+")) {
    // Just set the number without the plus
    nationalNumber.value = phoneNumber.slice(1);
  } else {
    nationalNumber.value = phoneNumber;
  }
};

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
  if (isDropdownOpen.value) {
    searchQuery.value = "";
    highlightedIndex.value = -1;
    focusTimeoutId = setTimeout(() => searchInputRef.value?.focus(), 50);
  }
};

const selectCountry = (country: Country) => {
  selectedCountry.value = country;
  isDropdownOpen.value = false;
  updateModelValue();
};

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    isDropdownOpen.value = false;
  }
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === "Escape") {
    isDropdownOpen.value = false;
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!isDropdownOpen.value) return;

  const countries = filteredCountries.value;
  const maxIndex = countries.length - 1;

  switch (event.key) {
    case "ArrowDown":
      event.preventDefault();
      highlightedIndex.value = Math.min(highlightedIndex.value + 1, maxIndex);
      scrollToHighlighted();
      break;
    case "ArrowUp":
      event.preventDefault();
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, 0);
      scrollToHighlighted();
      break;
    case "Enter":
      event.preventDefault();
      if (highlightedIndex.value >= 0 && highlightedIndex.value <= maxIndex) {
        selectCountry(countries[highlightedIndex.value]);
      }
      break;
    case "Home":
      event.preventDefault();
      highlightedIndex.value = 0;
      scrollToHighlighted();
      break;
    case "End":
      event.preventDefault();
      highlightedIndex.value = maxIndex;
      scrollToHighlighted();
      break;
  }
};

const scrollToHighlighted = () => {
  if (!countryListRef.value || highlightedIndex.value < 0) return;
  const items = countryListRef.value.querySelectorAll('[role="option"]');
  const highlightedItem = items[highlightedIndex.value] as HTMLElement;
  if (highlightedItem) {
    highlightedItem.scrollIntoView({ block: "nearest" });
  }
};

const updateModelValue = () => {
  emit("update:modelValue", fullPhoneNumber.value);
};

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value;

  // Remove any country code if user typed it
  value = value.replace(/^(\+|00)\d{1,4}\s*/, "");
  // Remove leading zeros
  value = value.replace(/^0+/, "");
  // Keep only digits and spaces
  value = value.replace(/[^\d\s]/g, "");

  nationalNumber.value = value;
  updateModelValue();
};

// Lifecycle
onMounted(() => {
  // Parse existing value or detect country
  if (props.modelValue) {
    parseExistingNumber(props.modelValue);
  } else {
    selectedCountry.value = detectCountry();
  }

  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  // Clear any pending timeouts
  if (focusTimeoutId) {
    clearTimeout(focusTimeoutId);
    focusTimeoutId = null;
  }
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleEscape);
});

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue && newValue !== fullPhoneNumber.value) {
      parseExistingNumber(newValue);
    }
  }
);

// Reset highlighted index when search query changes
watch(searchQuery, () => {
  highlightedIndex.value = filteredCountries.value.length > 0 ? 0 : -1;
});
</script>

<template>
  <div
    class="block w-full"
    role="group"
    :aria-labelledby="dropdownId + '-label'"
  >
    <span
      :id="dropdownId + '-label'"
      class="flex items-end justify-between text-custom-text pb-1 text-sm font-semibold"
    >
      <slot></slot>
    </span>
    <div class="flex" ref="dropdownRef">
      <!-- Country Code Dropdown -->
      <div class="relative">
        <button
          :id="dropdownId"
          type="button"
          class="flex items-center gap-1 px-3 py-2 min-h-12 border border-r-0 bg-gray-200 bg-opacity-70 rounded-l-md hover:bg-gray-300 transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
          :class="{
            'border-gray-300': !error,
            'border-custom-red': !!error,
          }"
          :disabled="disabled"
          :aria-expanded="isDropdownOpen"
          :aria-controls="listboxId"
          aria-haspopup="listbox"
          :aria-label="t('select_country') + ': ' + selectedCountry.name"
          @click.prevent="toggleDropdown"
          @keydown="handleKeydown"
        >
          <span class="text-lg" aria-hidden="true">{{
            selectedCountry.flag
          }}</span>
          <span class="text-sm text-gray-700">{{
            selectedCountry.callingCode
          }}</span>
          <svg
            class="w-4 h-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <!-- Dropdown Menu -->
        <div
          v-show="isDropdownOpen"
          :id="listboxId"
          class="absolute z-50 mt-1 w-72 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg"
          role="dialog"
          aria-modal="true"
          :aria-label="t('select_country')"
        >
          <!-- Search -->
          <div class="sticky top-0 bg-white p-2 border-b border-gray-100">
            <label :for="dropdownId + '-search'" class="sr-only">{{
              t("search_country")
            }}</label>
            <input
              :id="dropdownId + '-search'"
              ref="searchInputRef"
              v-model="searchQuery"
              type="text"
              role="combobox"
              :aria-expanded="isDropdownOpen"
              aria-autocomplete="list"
              :aria-controls="listboxId + '-list'"
              :aria-activedescendant="
                highlightedIndex >= 0
                  ? listboxId + '-option-' + highlightedIndex
                  : undefined
              "
              class="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              :placeholder="t('search_country')"
              @keydown="handleKeydown"
            />
          </div>

          <!-- Country List -->
          <ul
            :id="listboxId + '-list'"
            ref="countryListRef"
            class="py-1"
            role="listbox"
            :aria-label="t('countries')"
          >
            <li
              v-for="(country, index) in filteredCountries"
              :id="listboxId + '-option-' + index"
              :key="country.code"
              role="option"
              :aria-selected="selectedCountry.code === country.code"
              :class="[
                'w-full px-4 py-2 text-left text-sm flex items-center gap-3 cursor-pointer',
                highlightedIndex === index
                  ? 'bg-primary bg-opacity-10'
                  : 'hover:bg-gray-100',
                selectedCountry.code === country.code ? 'font-semibold' : '',
              ]"
              @click="selectCountry(country)"
              @mouseenter="highlightedIndex = index"
            >
              <span class="text-lg" aria-hidden="true">{{ country.flag }}</span>
              <span class="flex-1">{{ country.name }}</span>
              <span class="text-gray-500" aria-hidden="true">{{
                country.callingCode
              }}</span>
              <span class="sr-only">{{ country.callingCode }}</span>
            </li>
            <li
              v-if="filteredCountries.length === 0"
              class="px-4 py-2 text-sm text-gray-500"
              role="option"
              aria-disabled="true"
            >
              {{ t("no_results") }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Phone Number Input -->
      <input
        :id="dropdownId + '-input'"
        type="tel"
        :name="name"
        :placeholder="placeholder || t('phone_placeholder')"
        :required="required"
        :disabled="disabled"
        :value="nationalNumber"
        class="flex-1 block py-2 px-3 border bg-gray-200 bg-opacity-70 rounded-r-md placeholder-gray-500 focus:border-gray-500 transition outline-none shadow-sm min-h-12 focus:bg-white disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-custom-grey-2 focus:ring-2 focus:ring-primary focus:ring-offset-1"
        :class="{
          'border-gray-300': !error,
          'border-custom-red': !!error,
        }"
        autocomplete="tel-national"
        :aria-labelledby="dropdownId + '-label'"
        :aria-describedby="error ? dropdownId + '-error' : undefined"
        :aria-invalid="!!error"
        @input="handleInput"
      />
    </div>

    <span
      v-if="error"
      :id="dropdownId + '-error'"
      role="alert"
      aria-live="polite"
      class="flex items-center text-custom-red text-xs font-medium mt-1"
    >
      <svg class="svg-icon svg-attention mr-1.5" aria-hidden="true">
        <use xlink:href="#attention" width="100%" height="100%"></use>
      </svg>
      {{ error }}
    </span>
  </div>
</template>

<i18n lang="yaml">
en:
  search_country: "Search country..."
  phone_placeholder: "Phone number"
  select_country: "Select country"
  countries: "Countries"
  no_results: "No countries found"
nb:
  search_country: "Søk land..."
  phone_placeholder: "Telefonnummer"
  select_country: "Velg land"
  countries: "Land"
  no_results: "Ingen land funnet"
es:
  search_country: "Buscar país..."
  phone_placeholder: "Número de teléfono"
  select_country: "Seleccionar país"
  countries: "Países"
  no_results: "No se encontraron países"
uk:
  search_country: "Пошук країни..."
  phone_placeholder: "Номер телефону"
  select_country: "Виберіть країну"
  countries: "Країни"
  no_results: "Країни не знайдено"
pl:
  search_country: "Szukaj kraju..."
  phone_placeholder: "Numer telefonu"
  select_country: "Wybierz kraj"
  countries: "Kraje"
  no_results: "Nie znaleziono krajów"
</i18n>
